import dayjs from 'dayjs';
import {
  Calendar1,
  CalendarCheck2,
  CalendarDays,
  CalendarFold,
  CalendarMinus2,
  CalendarRange,
  CalendarX2,
  Clock,
} from 'lucide-react';
import { z } from 'zod';

import type { Dayjs } from 'dayjs';

export const dateRangePresets = [
  {
    key: 'today',
    menuLabel: 'Today',
    label: 'Today',
    icon: <Calendar1 size="1em" />,
    range: (): [Dayjs, Dayjs] => [dayjs().startOf('day'), dayjs().startOf('day')],
  },
  {
    key: 'yesterday',
    menuLabel: 'Yesterday',
    label: 'Yesterday',
    icon: <CalendarMinus2 size="1em" />,
    range: (): [Dayjs, Dayjs] => [
      dayjs().subtract(1, 'day').startOf('day'),
      dayjs().subtract(1, 'day').startOf('day'),
    ],
  },
  {
    key: '7d',
    menuLabel: '7 days',
    label: 'Last 7 days',
    icon: <CalendarRange size="1em" />,
    range: (): [Dayjs, Dayjs] => [
      dayjs().subtract(6, 'day').startOf('day'),
      dayjs().startOf('day'),
    ],
  },
  {
    key: '30d',
    menuLabel: '30 days',
    label: 'Last 30 days',
    icon: <CalendarDays size="1em" />,
    range: (): [Dayjs, Dayjs] => [
      dayjs().subtract(29, 'day').startOf('day'),
      dayjs().startOf('day'),
    ],
  },
  {
    key: '90d',
    menuLabel: '90 days',
    label: 'Last 90 days',
    icon: <CalendarFold size="1em" />,
    range: (): [Dayjs, Dayjs] => [
      dayjs().subtract(89, 'day').startOf('day'),
      dayjs().startOf('day'),
    ],
  },
  {
    key: 'this-month',
    menuLabel: 'This month',
    label: 'This month',
    icon: <CalendarCheck2 size="1em" />,
    range: (): [Dayjs, Dayjs] => [dayjs().startOf('month'), dayjs().startOf('day')],
  },
  {
    key: 'last-month',
    menuLabel: 'Last month',
    label: 'Last month',
    icon: <CalendarX2 size="1em" />,
    range: (): [Dayjs, Dayjs] => [
      dayjs().subtract(1, 'month').startOf('month'),
      dayjs().subtract(1, 'month').endOf('month').startOf('day'),
    ],
  },
  {
    key: 'this-year',
    menuLabel: 'This year',
    label: 'Year to date',
    icon: <Clock size="1em" />,
    range: (): [Dayjs, Dayjs] => [dayjs().startOf('year'), dayjs().startOf('day')],
  },
];

export const dateRangeSearchSchema = z.object({
  datePreset: z.string().optional(),
  fromDate: z.string().optional(),
  toDate: z.string().optional(),
  compare: z.boolean().optional(),
});

export const resolveDateRange = ({
  datePreset = '30d',
  fromDate,
  toDate,
}: z.infer<typeof dateRangeSearchSchema>) => {
  const preset = fromDate && toDate ? undefined : dateRangePresets.find(({ key }) => key === datePreset);
  const [from, to] = preset?.range() ?? [dayjs(fromDate), dayjs(toDate)];
  return { preset, from, to };
};
